/**
 * FenestrationMesh Component
 *
 * Renders a fenestration (window, door, glass door) as a 3D mesh.
 */

import { useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { FenestrationGeometry, ViewMode } from '../types';
import { FENESTRATION_COLORS, SELECTION_COLOR, HOVER_COLOR } from '../colorSchemes';

interface FenestrationMeshProps {
  fenestration: FenestrationGeometry;
  viewMode: ViewMode;
  isSelected: boolean;
  onSelect: (id: string, ctrlKey: boolean) => void;
  onDoubleClick?: (id: string) => void;
}

/**
 * Project 3D vertices to 2D plane for Shape creation.
 */
function projectTo2D(
  vertices: THREE.Vector3[],
  normal: THREE.Vector3
): { points2D: THREE.Vector2[]; basis: { u: THREE.Vector3; v: THREE.Vector3 }; origin: THREE.Vector3 } {
  if (vertices.length < 3) {
    return { points2D: [], basis: { u: new THREE.Vector3(1, 0, 0), v: new THREE.Vector3(0, 1, 0) }, origin: new THREE.Vector3() };
  }

  // We've already checked vertices.length >= 3
  const [v0, v1] = vertices as [THREE.Vector3, THREE.Vector3];

  const origin = v0.clone();
  const u = new THREE.Vector3().subVectors(v1, v0).normalize();
  const v = new THREE.Vector3().crossVectors(normal, u).normalize();

  const points2D = vertices.map((vertex) => {
    const relative = new THREE.Vector3().subVectors(vertex, origin);
    return new THREE.Vector2(relative.dot(u), relative.dot(v));
  });

  return { points2D, basis: { u, v }, origin };
}

export function FenestrationMesh({
  fenestration,
  viewMode,
  isSelected,
  onSelect,
  onDoubleClick,
}: FenestrationMeshProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Convert vertices to Three.js
  // IDF uses z-up, Three.js uses y-up, so we swap y and z
  const threeVertices = useMemo(
    () => fenestration.vertices.map((v) => new THREE.Vector3(v.x, v.z, v.y)),
    [fenestration.vertices]
  );

  const threeNormal = useMemo(
    () => new THREE.Vector3(fenestration.normal.x, fenestration.normal.z, fenestration.normal.y).normalize(),
    [fenestration.normal]
  );

  // Create the geometry
  const geometry = useMemo(() => {
    if (threeVertices.length < 3) return null;

    const { points2D, basis, origin } = projectTo2D(threeVertices, threeNormal);

    const shape = new THREE.Shape();
    shape.setFromPoints(points2D);
    shape.closePath();

    const shapeGeometry = new THREE.ShapeGeometry(shape);

    // Transform back to 3D
    const positionAttr = shapeGeometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < positionAttr.count; i++) {
      const x2D = positionAttr.getX(i);
      const y2D = positionAttr.getY(i);

      const point3D = origin
        .clone()
        .add(basis.u.clone().multiplyScalar(x2D))
        .add(basis.v.clone().multiplyScalar(y2D));

      positionAttr.setXYZ(i, point3D.x, point3D.y, point3D.z);
    }

    positionAttr.needsUpdate = true;
    shapeGeometry.computeVertexNormals();

    return shapeGeometry;
  }, [threeVertices, threeNormal]);

  // Get base color
  const baseColor = FENESTRATION_COLORS[fenestration.type];

  // Display color based on state
  const displayColor = isSelected ? SELECTION_COLOR : isHovered ? HOVER_COLOR : baseColor;

  // Material properties
  const materialProps = useMemo(() => {
    switch (viewMode) {
      case 'wireframe':
        return {
          color: displayColor,
          wireframe: true,
          transparent: false,
          opacity: 1,
        };
      case 'x-ray':
        return {
          color: displayColor,
          wireframe: false,
          transparent: true,
          opacity: 0.2,
        };
      case 'shaded':
      default:
        return {
          color: displayColor,
          wireframe: false,
          transparent: true,
          opacity: 0.7,
        };
    }
  }, [viewMode, displayColor]);

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSelect(fenestration.id, event.ctrlKey || event.metaKey);
  };

  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onDoubleClick?.(fenestration.id);
  };

  if (!geometry) return null;

  return (
    <group>
      <mesh
        ref={meshRef}
        geometry={geometry}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onPointerOver={() => { setIsHovered(true); }}
        onPointerOut={() => { setIsHovered(false); }}
        userData={{ fenestrationId: fenestration.id, type: 'fenestration', name: fenestration.name }}
      >
        <meshLambertMaterial
          color={materialProps.color}
          wireframe={materialProps.wireframe}
          transparent={materialProps.transparent}
          opacity={materialProps.opacity}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Edge lines */}
      {viewMode === 'shaded' && (
        <lineSegments geometry={new THREE.EdgesGeometry(geometry)}>
          <lineBasicMaterial color="#0066cc" linewidth={1} />
        </lineSegments>
      )}
    </group>
  );
}
